import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';

type PromotionStatus = 'Pending' | 'Promoted' | 'Failed' | 'On Hold';

interface Student {
  id: number;
  adm_code: string;
  name: string;
  gender: string;
  rollNo: string;
  fee_due: number;
  source_class: string;
  target_class: string;
  result: 'Pass' | 'Fail' | 'Pending';
  status: PromotionStatus;
  remarks: string;
  selected: boolean;
}

@Component({
  selector: 'app-student-migration-promotion',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './student-migration-promotion.component.html',
  styleUrl: './student-migration-promotion.component.css'
})
export class StudentMigrationPromotionComponent {
  dataService     = inject(DataService);
  loading = false;
  user: any = null;
  acd_session = '';

  classes: any = this.dataService.classes;
  source_class: any = this.dataService.classes;
  targetOptions: any = this.dataService.classes;
  searchText = '';
  resultFilter = 'All';
  selectAll = false;
  showConfirm = false;
  notice = '';
  noticeType: 'success' | 'warning' | 'info' = 'info';

  // Demo data only. Replace this array with the students returned by your API.
  students: Student[] = [];

  get sourceStudents(): Student[] {
    return this.students.filter(s => s.source_class === this.source_class);
  }

  get filteredStudents(): Student[] {
    const query = this.searchText.trim().toLowerCase();
    return this.sourceStudents.filter(s => {
      const matchesQuery = !query ||
        s.name.toLowerCase().includes(query) ||
        s.adm_code.toLowerCase().includes(query) ||
        s.rollNo.toLowerCase().includes(query);
      const matchesResult = this.resultFilter === 'All' || s.result === this.resultFilter;
      return matchesQuery && matchesResult;
    });
  }

  get selectedStudents(): Student[] {
    return this.sourceStudents.filter(s => s.selected);
  }

  get passedCount(): number {
    return this.sourceStudents.filter(s => s.result === 'Pass').length;
  }

  get pendingCount(): number {
    return this.sourceStudents.filter(s => s.status === 'Pending').length;
  }

  // get targetOptions(): string[] {
  //   const sourceIndex = this.classes.indexOf(this.source_class);
  //   // return sourceIndex >= 0 ? this.classes.filter((_, i) => i > sourceIndex) : [];
  //   return sourceIndex || [];
  // }

  async ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");

    if(erp_rem && !this.user) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
      this.acd_session = this.user.session + '-' + ((parseInt(this.user.session) + 1) - 2000);
    }

    this.loading = true;
    await this.dataService.getAllRecords('adm_rosters').then((res: any) => {
      if(res && res.length) {
        for(let i=0; i<res.length; i++) {
          this.students.push({ 
            id: res[i].id, 
            adm_code: res[i].adm_code, 
            name: res[i].std_name, 
            gender: res[i].gender, 
            rollNo: res[i].roll_no, 
            fee_due: res[i].fee_due,
            source_class: res[i].class_name, 
            target_class: res[i].class_name, 
            result: res[i].promo_status=='Promoted' ? 'Pass' : res[i].result, 
            status: res[i].promo_status || 'Pending', 
            remarks: res[i].remarks || '',
            selected: false 
          });
        }
      }
    });
  }

  onSourceClassChange(): void {
    this.selectAll = false;
    this.notice = '';
  }

  toggleSelectAll(): void {
    this.filteredStudents.forEach(s => {
      if(s.status != 'Promoted') {
        s.selected = this.selectAll;
      }
    });
  }

  syncSelectAll(): void {
    this.selectAll = this.filteredStudents.length > 0 &&
      this.filteredStudents.every(s => s.selected);
  }

  setTargetForSelected(target_class: string): void {
    if (!target_class) return;
    this.selectedStudents.forEach(s => s.target_class = target_class);
    this.notice = `${this.selectedStudents.length} student(s) assigned to ${target_class}.`;
    this.noticeType = 'info';
  }

  promoteSelected(): void {
    const selected = this.selectedStudents;
    if (!selected.length) {
      this.setNotice('Select at least one student first.', 'warning');
      return;
    }
    const invalid = selected.filter(s => s.result !== 'Pass' || !s.target_class || s.target_class === s.source_class);
    if (invalid.length) {
      this.setNotice('Only students with a Pass result can be promoted. Check their result and target class.', 'warning');
      return;
    }
    this.showConfirm = true;
  }

  confirmPromotion(): void {
    const selected = this.selectedStudents;
    selected.forEach(s => {
      s.status = 'Promoted';
      s.remarks = 'Promoted from class ' + this.dataService.getClassName(s.source_class) + ' to ' + this.dataService.getClassName(s.target_class) + ' for session ' + (parseInt(this.user.session)+1);
      s.selected = false;
    });

    const jsonData = {
      'erp_key': this.user.erp_key,
      'cur_session': parseInt(this.user.session),
      'new_session': parseInt(this.user.session)+1,
      'ssn_data': selected
    };

    this.doSave('ssn_adm_bulk', jsonData);

    this.showConfirm = false;
    this.selectAll = false;
    this.setNotice(`${selected.length} student(s) promoted successfully. Connect this action to your API to save changes permanently.`, 'success');
  }

  markSelectedAsFailed(): void {
    const selected = this.selectedStudents;
    if (!selected.length) {
      this.setNotice('Select at least one student first.', 'warning');
      return;
    }
    selected.forEach(s => {
      s.status = 'Failed';
      s.selected = false;
    });

    this.selectAll = false;
    this.setNotice(`${selected.length} student(s) marked as failed.`, 'info');
  }

  setNotice(message: string, type: 'success' | 'warning' | 'info'): void {
    this.notice = message;
    this.noticeType = type;
  }

  statusClass(status: PromotionStatus): string {
    return {
      Pending: 'status-pending',
      Promoted: 'status-promoted',
      Failed: 'status-failed',
      'On Hold': 'status-hold'
    }[status];
  }

  trackById(_: number, student: Student): number {
    return student.id;
  }

  // ── ACTIONS ─────────────────────────────────────────
  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }


}
